import { FlashbarMessage, StickyNotificationsHookContext, UseStickyNotificationsHook } from "@amzn/xb-hooks";
import find from "lodash/find";
import { PropsWithChildren } from "react";
import { create } from "zustand";

interface Shape {
  messages: FlashbarMessage[];
  push: (message: FlashbarMessage) => void;
  set: (messages: FlashbarMessage[]) => void;
  removePageScopedMessages: () => void;
}

export const useStickyNotificationsStore = create<Shape>()((set) => ({
  messages: [],
  push: (message: FlashbarMessage) =>
    set((state) => ({
      messages: find(state.messages, ["messageId", message.messageId])
        ? state.messages
        : [...state.messages, { ...message, scope: message.scope ?? "app" }],
    })),
  set: (messages: FlashbarMessage[]) =>
    set({
      messages: messages.map((message) => ({
        ...message,
        scope: message.scope ?? "app",
      })),
    }),
  removePageScopedMessages: () =>
    set((state) => ({
      messages: state.messages.filter((message) => message.scope === "app"),
    })),
}));

const useStickyNotifications: UseStickyNotificationsHook = () => {
  const { messages, push, set } = useStickyNotificationsStore();

  return {
    messages,
    push: (m: FlashbarMessage) => (push(m), true),
    pushMessage: (m: FlashbarMessage) => (push(m), true), // alias for convenience
    setMessages: (m: FlashbarMessage[]) => (set(m), true),
  };
};

export const UseStickyNotificationsHookProvider = ({ children }: PropsWithChildren) => (
  <StickyNotificationsHookContext.Provider value={useStickyNotifications}>
    {children}
  </StickyNotificationsHookContext.Provider>
);
